<template>

    <div class="page">

        <!-- KtDatatable Implementation -->
        <kt-datatable
            :model="workModel"
            :data="rows"
            :filter="datatableFilter"
            :meta="{ tableClass: '-table-bordered', rowClass: getRowClass }"
            :update-function="handleDatatableUpdate"
            :hide-search="false"
            :hide-page-size="props.embedded"
            @cell-click="handleCellClick"
            storage-key="work-list"
        >
            <template #header_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectAll"
                        @change="toggleSelectAll"
                        @click.stop
                        title="Select all on this page"
                    />
                </div>
            </template>
            
            <template #view_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectedRows.includes(scope.row.Id)"
                        @change="toggleRowSelection(scope.row.Id)"
                        @click.stop
                    />
                </div>
            </template>
            
            <template #filters="scope">
                <div class="col" v-if="!props.hideFilters">
                    <div class="row">
                        <div class="col-auto">
                            <client-multi-check 
                                label="Clients"
                                :model-value="currentFilter.client"
                                @update:model-value="(value: any) => setFilter({client: value})"
                            ></client-multi-check>
                        </div>
                        <div class="col-auto">
                            <tech-multi-check 
                                label="Technicians"
                                :model-value="currentFilter.technician"
                                @update:model-value="(value: any) => setFilter({technician: value})"
                            ></tech-multi-check>
                        </div>
                        <div class="col-auto">
                            <kt-date-range-select
                                label="Date Range"
                                v-model="dateRange"
                            ></kt-date-range-select>
                        </div>
                    </div>
                </div>
            </template>

            <template #under-header>
                <!-- Bulk Actions Toolbar -->
                <div v-if="selectedRows.length > 0" class="mb-0 p-2 actions-row">
                    <div class="d-flex align-items-center gap-2">
                        <span class="fw-bold text-primary">{{ selectedRows.length }} selected</span>
                        <button 
                            class="btn btn-sm btn-outline-danger"
                            @click="showDeleteModal = true"
                            title="Delete Selected"
                        >
                            <i class="fas fa-trash"></i> Delete
                        </button>
                        <button 
                            class="btn btn-sm btn-outline-secondary me-auto"
                            @click="clearSelection"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>
                
                <div v-if="hasActiveFilters && !props.hideFilters" class="d-flex flex-wrap gap-2 p-2">
                    <span class="text-muted small me-2">Active filters:</span>
                    
                    <!-- Client filter badges -->
                    <span v-for="clientId in currentFilter.client" 
                          :key="`client-${clientId}`" 
                          class="badge bg-primary d-flex align-items-center">
                        Client: {{ getClientName(clientId) }}
                        <button type="button" 
                                class="btn-close btn-close-white ms-2" 
                                @click="removeClientFilter(clientId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Technician filter badges -->
                    <span v-for="techId in currentFilter.technician" 
                          :key="`tech-${techId}`" 
                          class="badge bg-success d-flex align-items-center">
                        Technician: {{ getTechName(techId) }}
                        <button type="button" 
                                class="btn-close btn-close-white ms-2" 
                                @click="removeTechFilter(techId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Date range filter badge -->
                    <span v-if="currentFilter.startDate || currentFilter.endDate" 
                          class="badge bg-info d-flex align-items-center">
                        Date: {{ currentFilter.startDate }} to {{ currentFilter.endDate }}
                        <button type="button" 
                                class="btn-close btn-close-white ms-2" 
                                @click="removeDateFilter()"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Search term badge -->
                    <span v-if="currentFilter.searchTerm"
                          class="badge bg-secondary d-flex align-items-center">
                        Search: {{ currentFilter.searchTerm }}
                        <button type="button" 
                                class="btn-close btn-close-white ms-2" 
                                @click="setFilter({ searchTerm: '' })"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Clear all filters -->
                    <button v-if="hasActiveFilters" 
                            class="btn btn-sm btn-outline-secondary ms-2" 
                            @click="clearAllFilters()">
                      Clear All
                    </button>
                </div>
            </template>

            <template #after-search="scope">
                <div class="d-flex gap-2">
                    <button @click="refresh()" 
                        class="btn btn-sm btn-outline-secondary"
                    >Refresh <i class="fa fa-sync"></i></button>

                    <button 
                        v-if="!props.embedded"
                        @click="showExportModal = true" 
                        class="btn btn-sm btn-outline-success"
                    >Export <i class="fa fa-download"></i></button>

                    <button 
                        v-if="!props.disableActions"
                        @click="newRecord()" 
                        class="btn btn-sm btn-outline-primary"
                    >New <i class="fa fa-plus"></i></button>
                </div>
            </template>

            <template #view_actions="scope">
                <kt-dropmenu v-model:visible="actionDropdown['row_' + scope.row.Id]">
                    <template #button>
                        <button
                            class="btn -btn-outline-primary btn-sm"
                            @click="actionDropdown['row_' + scope.row.Id] = !actionDropdown['row_' + scope.row.Id]"
                        >
                            <span><i class="fas fa-ellipsis-v"></i></span>
                        </button>
                    </template>
                    <template #dropmenu>
                        <ul class="dropdown-menu show">
                            <li>
                                <label class="dropdown-item">
                                    <button class="btn btn-sm btn-outline-primary form-control" @click="editRecord(scope.row.Id)">
                                        <i class="fa fa-edit"></i> Edit
                                    </button>
                                </label>
                            </li>
                            <li>
                                <label class="dropdown-item">
                                    <button class="btn btn-sm btn-outline-danger form-control" @click="deleteRecord(scope.row.Id)">
                                        <i class="fa fa-trash"></i> Remove
                                    </button>
                                </label>
                            </li>
                        </ul>
                    </template>
                </kt-dropmenu>
            </template>
            
            <template #view_string="scope">
                <span v-if="scope.column.name === 'Contact'">
                    <a href="javascript:void(0);" @click.stop="editClient(scope.row.Contact?.Id)">
                        {{ scope.row.Contact?.Code || 'N/A' }}
                    </a>
                </span>
                <span v-else-if="scope.column.name === 'Company'">
                    <a href="javascript:void(0);" @click.stop="editClient(scope.row.Contact?.Id)">
                        {{ scope.row.Contact?.Company || 'N/A' }}
                    </a>
                </span>
                <span v-else-if="scope.column.name === 'RequestId'">
                    <a v-if="scope.row.Request?.Id" href="javascript:void(0);" @click.stop="editJob(scope.row.Request?.Id)">
                        {{ scope.row.Request?.Id }}
                    </a>
                    <span v-else>N/A</span>
                </span>
                <span v-else-if="scope.column.name === 'TechnicianName'">
                    <a href="javascript:void(0);" @click.stop="editTech(scope.row.Technician?.Id)">
                        {{ scope.row.Technician?.TechnicianName || 'N/A' }}
                    </a>
                </span>
                <span v-else-if="scope.column.name === 'InvoiceNumber'">
                    <a v-if="scope.row.WorkOrder?.Id" href="javascript:void(0);" @click.stop="Emitter.emit('edit:workorder', scope.row.WorkOrder.Id)" class="badge bg-info text-decoration-none">
                        <i class="fas fa-file-invoice"></i> {{ scope.row.WorkOrder?.InvoiceNumber || '#' + scope.row.WorkOrder?.Id }}
                    </a>
                </span>
                <span v-else-if="scope.isTruncated">
                    {{ scope.truncatedValue }}
                    <button 
                        class="btn btn-sm btn-link p-0 ms-1" 
                        @click.stop="scope.showFullText(scope.value)"
                        title="View full text"
                    >
                        <i class="fas fa-search"></i>
                    </button>
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>
            
            <template #view_number="scope">
                <span v-if="scope.column.name === 'TaskLocation'">
                    {{ getLocationName(scope.value) }}
                </span>
                <span v-else-if="scope.column.name === 'Status'">
                    <span v-if="scope.value" :class="getStatusBadgeClass(scope.value)" class="badge">
                        <i :class="getStatusIcon(scope.value)" class="me-1"></i>
                        {{ getStatusName(scope.value) }}
                    </span>
                    <span v-else class="badge bg-light text-dark"><i class="fas fa-circle me-1"></i>No Status</span>
                </span>
                <span v-else-if="scope.column.name === 'InvoiceAmount' || scope.column.name === 'ShouldBill' || scope.column.name === 'PaidAmount'">
                    ${{ (scope.value || 0).toFixed(2) }}
                </span>
                <span v-else>
                    {{ scope.value || 0 }}
                </span>
            </template>
        </kt-datatable>
        
        <!-- Bulk Delete Confirmation Modal -->
        <kt-modal
            :visible="showDeleteModal"
            title="Confirm Bulk Delete"
            @dismiss="showDeleteModal = false"
        >
            <template #body>
                <div class="container-fluid">
                    <p class="text-danger mb-3">
                        <i class="fas fa-exclamation-triangle"></i>
                        Are you sure you want to delete {{ selectedRows.length }} work entry(s)?
                    </p>
                </div>
            </template>
            <template #footer>
                <div class="kt-modal-actions">
                    <button class="btn btn-outline-secondary" @click="showDeleteModal = false">
                        <X :size="16" class="me-1" />Cancel
                    </button>
                    <button class="btn btn-danger" @click="confirmBulkDelete">
                        <Trash2 :size="16" class="me-1" />Delete
                    </button>
                </div>
            </template>
        </kt-modal>
    </div>
    
    <router-view name="modal"></router-view>
    
    <!-- Embedded Edit Modal -->
    <work-edit-embedded 
        v-if="props.embedded && showEditModal" 
        :record-id="editingRecordId"
        :request-id="props.requestId"
        :client-id="props.clientId"
        :tech-id="props.techId"
        @close="closeEditModal"
    />
    
</template>

<style scoped>
/* Row shading based on status - applied to td cells */
:deep(.row-status-completed td) {
    background-color: rgba(25, 135, 84, 0.08) !important;
}

:deep(.row-status-completed:hover td) {
    background-color: rgba(25, 135, 84, 0.15) !important;
}

:deep(.row-status-pending td) {
    background-color: rgba(255, 193, 7, 0.08) !important;
}

:deep(.row-status-pending:hover td) {
    background-color: rgba(255, 193, 7, 0.15) !important;
}

:deep(.row-status-progress td) {
    background-color: rgba(13, 110, 253, 0.08) !important;
}

:deep(.row-status-progress:hover td) {
    background-color: rgba(13, 110, 253, 0.15) !important;
}

:deep(.row-status-cancelled td) {
    background-color: rgba(220, 53, 69, 0.08) !important;
}

:deep(.row-status-cancelled:hover td) {
    background-color: rgba(220, 53, 69, 0.15) !important;
}

:deep(.row-status-scheduled td) {
    background-color: rgba(13, 202, 240, 0.08) !important;
}

:deep(.row-status-scheduled:hover td) {
    background-color: rgba(13, 202, 240, 0.15) !important;
}

:deep(.row-status-draft td) {
    background-color: rgba(108, 117, 125, 0.08) !important;
}

:deep(.row-status-draft:hover td) {
    background-color: rgba(108, 117, 125, 0.15) !important;
}

/* Datatable link hover effect - blue theme with edit icon badge */
:deep(td a) {
    text-decoration: none;
    color: #333;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid transparent;
    transition: all 0.2s ease-in-out;
    display: inline-block;
    position: relative;
}

:deep(td a:hover) {
    background-color: #15486A;
    color: #fff;
}

/* Edit icon badge in top-right corner on hover */
:deep(td a:hover::after) {
    content: '\f044';
    font-family: 'Font Awesome 5 Free';
    font-weight: 900;
    position: absolute;
    top: -6px;
    right: -6px;
    background: #3B9796;
    color: white;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    border: 2px solid white;
}
</style>
    
<script setup lang="ts">

import { ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter, RouterView } from "vue-router";
import Api from "@/services/ApiService";
import { ApiResponse } from "@/services/ApiService";
import { TimeModel } from "@/models/TaskModel";
import Cache from "@/services/Cache";
import KtDatatable from "@/components/UI/KtDatatable.vue";
import { KtDatatableModelProp } from "@/components/UI/KtDataTable";
import ClientMultiCheck from "@/components/Clients/ClientMultiCheck.vue";
import TechMultiCheck from "@/components/Techs/TechMultiCheck.vue";
import KtDateRangeSelect from "@/components/UI/KtDateRangeSelect.vue";
import KtDropmenu from "@/components/UI/KtDropmenu.vue";
import KtModal from "@/components/UI/KtModal.vue";
import { X, Trash2 } from 'lucide-vue-next';
import WorkEditEmbedded from "@/components/Work/WorkEditEmbedded.vue";
import Emitter from "@/services/Emitter";
import Alert from "@/services/Alert";
import { useClientStore } from "@/stores/ClientStore";
import { useTechStore } from "@/stores/TechStore";
import { useTaskStatusStore } from "@/stores/TaskStatusStore";
import { storeToRefs } from "pinia";
import { useDatatableSettings } from "@/composables";

const route = useRoute();
const router = useRouter();

// Datatable settings for persisting user preferences
const { 
    loadSettings: loadDatatableSettings, 
    saveFilters: saveDatatableFilters 
} = useDatatableSettings('WorkDatatable');

// Props
const props = defineProps({
    requestId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: [Number, String],
        default: null
    },
    techId: {
        type: [Number, String],
        default: null
    },
    hideFilters: {
        type: Boolean,
        default: false
    },
    embedded: {
        type: Boolean,
        default: false
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['count-loaded']);

// Store access
const ClientStore = useClientStore();
const TechStore = useTechStore();

// Computed store lists
const ClientList = computed(() => Object.values(ClientStore.list));
const TechList = computed(() => Object.values(TechStore.list));

// Data state
const total = ref(0);
const rows = ref<TimeModel[]>([]);

// Selection state
const selectedRows = ref<number[]>([]);
const selectAll = ref(false);
const showDeleteModal = ref(false);
const showExportModal = ref(false);

// ===== STORES =====

// Task Status Store
const taskStatusStore = useTaskStatusStore();
const { list: TaskStatusList } = storeToRefs(taskStatusStore);
taskStatusStore.load();

// ===== FILTER SYSTEM =====

// Filter state interface
interface WorkListFilter {
    client: number[];
    technician: number[];
    user: string;
    searchTerm: string;
    startDate: string;
    endDate: string;
    page: number;
    size: number;
    sort: Array<Record<string, 'ASC' | 'DESC'>>;
}

// Current filter state (reactive)
const currentFilter = ref<WorkListFilter>({
    client: [],
    technician: [],
    user: '',
    searchTerm: '',
    startDate: '',
    endDate: '',
    page: 1,
    size: props.embedded ? 9999 : 25,
    sort: []
});

// Datatable filter for pagination (derived from currentFilter)
const datatableFilter = computed(() => ({
    page: currentFilter.value.page,
    size: currentFilter.value.size,
    total: total.value,
    search: currentFilter.value.searchTerm,
    sort: currentFilter.value.sort
}));

// Date range picker (derived from currentFilter)
const dateRange = computed({
    get: () => ({
        startDate: currentFilter.value.startDate || null,
        endDate: currentFilter.value.endDate || null
    }),
    set: (value) => {
        setFilter({
            startDate: value.startDate || '',
            endDate: value.endDate || ''
        });
    }
});

// Check if any filters are active
const hasActiveFilters = computed(() => {
    return (currentFilter.value.client && currentFilter.value.client.length > 0) ||
           (currentFilter.value.technician && currentFilter.value.technician.length > 0) ||
           currentFilter.value.searchTerm ||
           currentFilter.value.startDate !== '' ||
           currentFilter.value.endDate !== '';
});

// ===== URL & FILTER UTILITIES =====

// Parse sort parameter from URL string to KtDatatable sort array format  
const parseSortFromUrl = (sortParam?: string): Array<Record<string, 'ASC' | 'DESC'>> => {
    if (!sortParam) return [];
    
    return sortParam.split(',').map(sortField => {
        const trimmed = sortField.trim();
        if (trimmed.startsWith('-')) {
            const column = trimmed.substring(1);
            return { [column]: 'DESC' as const };
        } else {
            return { [trimmed]: 'ASC' as const };
        }
    });
};

// Parse URL params to filter object
const parseUrlToFilter = (): WorkListFilter => {
    const routeQuery = route.query;
    
    return {
        client: Array.isArray(routeQuery.client) 
            ? routeQuery.client.filter(c => c !== null).map(c => parseInt(c as string))
            : (routeQuery.client ? [parseInt(routeQuery.client as string)] : []),
        technician: Array.isArray(routeQuery.technician) 
            ? routeQuery.technician.filter(t => t !== null).map(t => parseInt(t as string))
            : (routeQuery.technician ? [parseInt(routeQuery.technician as string)] : []),
        user: (routeQuery.user as string) || '',
        searchTerm: (routeQuery.searchTerm as string) || '',
        startDate: (routeQuery.startDate as string) || '',
        endDate: (routeQuery.endDate as string) || '',
        page: parseInt((routeQuery.page as string) || '1'),
        size: parseInt((routeQuery.size as string) || '25'),
        sort: parseSortFromUrl(routeQuery.sort as string)
    };
};

// Build URL params from filter object
const buildUrlFromFilter = (filter: WorkListFilter): string => {
    const params = new URLSearchParams();
    
    // Add array parameters (with null checks)
    filter.client?.forEach(c => c && params.append('client', c.toString()));
    filter.technician?.forEach(t => t && params.append('technician', t.toString()));
    
    // Add single parameters
    if (filter.user) params.set('user', filter.user);
    if (filter.searchTerm) params.set('searchTerm', filter.searchTerm);
    if (filter.startDate) params.set('startDate', filter.startDate);
    if (filter.endDate) params.set('endDate', filter.endDate);
    
    // Add sort parameter
    if (filter.sort && filter.sort.length > 0) {
        const sortString = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        }).join(',');
        params.set('sort', sortString);
    }
    
    // Always include page parameter
    if (filter.page !== undefined) {
        params.set('page', filter.page.toString());
    }
    if (filter.size !== undefined && filter.size !== 25) {
        params.set('size', filter.size.toString());
    }
    
    return params.toString();
};

// Save filter to Cache service for persistence
const saveFilterToStorage = async (filter: WorkListFilter) => {
    try {
        await Cache.set('work-list-filter', filter);
    } catch (error) {
        console.error('Failed to save filter to cache:', error);
    }
};

// Load filter from Cache service as fallback
const loadFilterFromStorage = async (): Promise<WorkListFilter> => {
    try {
        const stored = await Cache.get('work-list-filter');
        if (stored) {
            return stored;
        }
    } catch (error) {
        console.error('Failed to load filter from cache:', error);
    }
    
    // Return default filter if nothing in cache
    return {
        client: [],
        technician: [],
        user: '',
        searchTerm: '',
        startDate: '',
        endDate: '',
        page: 1,
        size: 25,
        sort: []
    };
};

// ===== API INTEGRATION =====

// Convert filter to API parameters
const buildApiParams = (filter: WorkListFilter) => {
    const page = Math.max(1, filter.page || 1);
    
    const params: any = {
        Page: page - 1,
        Size: filter.size || 25,
        IncludeRequest: true,
        IncludeContact: true,
        TimeType: 'Work'  // Always filter to work entries only
    };
    
    // If requestId prop is provided, filter by that request only
    if (props.requestId) {
        params.RequestId = props.requestId;
        return params;
    }
    
    // Add client CustomerNumbers directly
    if (filter.client && filter.client.length > 0) {
        params.CustomerNumber = filter.client;
    }
    
    // Add technician filter
    if (filter.technician && filter.technician.length > 0) params.TechId = filter.technician;
    
    // Add single filters
    if (filter.user) params.TechId = filter.user;
    if (filter.searchTerm) params.SearchTerm = filter.searchTerm;
    if (filter.startDate) {
        params.StartDate = new Date(filter.startDate + " 00:00:00");
    }
    if (filter.endDate) {
        params.EndDate = new Date(filter.endDate + " 23:59:59");
    }
    
    // Add OrderBy parameter for server-side sorting
    if (filter.sort && filter.sort.length > 0) {
        const orderByParts = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        });
        params.OrderBy = orderByParts.join(',');
    }
    
    return params;
};

// Load data from API using current filter
const loadData = async () => {
    try {
        const params = buildApiParams(currentFilter.value);
        
        const response: ApiResponse<TimeModel[]> = await Api.loadTimes(params, false);
        
        rows.value = response.Data || [];
        total.value = response.Meta?.Total || 0;
        
        // Emit count for parent components (e.g., JobForm tabs)
        emit('count-loaded', rows.value.length);
    } catch (error) {
        console.error('Failed to load data:', error);
        rows.value = [];
        total.value = 0;
    }
};

// ===== FILTER UPDATE HANDLERS =====

// Handle datatable pagination/sorting updates
const handleDatatableUpdate = (filterUpdate: any) => {
    // If embedded mode (requestId prop), update filter directly without routing
    if (props.requestId) {
        if (filterUpdate.page !== undefined) currentFilter.value.page = filterUpdate.page;
        if (filterUpdate.size !== undefined) currentFilter.value.size = filterUpdate.size;
        if (filterUpdate.sort !== undefined) currentFilter.value.sort = filterUpdate.sort || [];
        if (filterUpdate.search !== undefined) currentFilter.value.searchTerm = filterUpdate.search;
        loadData();
        return;
    }
    
    // Create a temporary filter object for URL building
    const tempFilter = { ...currentFilter.value };
    if (filterUpdate.page !== undefined) tempFilter.page = filterUpdate.page;
    if (filterUpdate.size !== undefined) tempFilter.size = filterUpdate.size;
    if (filterUpdate.sort !== undefined) tempFilter.sort = filterUpdate.sort || [];
    if (filterUpdate.search !== undefined) tempFilter.searchTerm = filterUpdate.search;
    
    // Build URL and navigate directly
    const urlParams = buildUrlFromFilter(tempFilter);
    const newUrl = `/work/list${urlParams ? '?' + urlParams : ''}`;
    
    router.replace(newUrl);
};

// Helper to check if saved filters have any non-empty values
const hasNonEmptyFilterValues = (filters: any): boolean => {
    if (!filters) return false;
    
    if (Array.isArray(filters.client) && filters.client.length > 0) return true;
    if (Array.isArray(filters.technician) && filters.technician.length > 0) return true;
    if (filters.searchTerm && filters.searchTerm.trim() !== '') return true;
    if (filters.startDate && filters.startDate.trim() !== '') return true;
    if (filters.endDate && filters.endDate.trim() !== '') return true;
    
    return false;
};

// Initialize filter on component mount
const initializeFilter = async (): Promise<void> => {
    // If embedded mode (requestId prop), skip URL initialization
    if (props.requestId || props.embedded) {
        return;
    }
    
    // Check if we have URL params
    const hasUrlParams = Object.keys(route.query).length > 0;
    
    if (hasUrlParams) {
        // URL has params - use them and save to DB
        currentFilter.value = parseUrlToFilter();
        saveFilterToStorage(currentFilter.value);
        saveDatatableFilters(currentFilter.value);
    } else {
        // Blank URL - try to load saved filters from database
        const dbSettings = await loadDatatableSettings();
        
        if (dbSettings?.filters && hasNonEmptyFilterValues(dbSettings.filters)) {
            currentFilter.value = {
                client: dbSettings.filters.client || [],
                technician: dbSettings.filters.technician || [],
                user: dbSettings.filters.user || '',
                searchTerm: dbSettings.filters.searchTerm || '',
                startDate: dbSettings.filters.startDate || '',
                endDate: dbSettings.filters.endDate || '',
                page: 1,
                size: dbSettings.filters.size || 25,
                sort: dbSettings.filters.sort || []
            };
        } else {
            // Fall back to cache or defaults
            currentFilter.value = await loadFilterFromStorage();
        }
        
        // Update URL to reflect loaded filter
        const urlParams = buildUrlFromFilter(currentFilter.value);
        const newUrl = `/work/list${urlParams ? '?' + urlParams : ''}`;
        router.replace(newUrl);
    }
};

// setFilter - called by filter controls to update filter state
const setFilter = (updates: Partial<WorkListFilter>) => {
    // If embedded mode (requestId prop), ignore filter updates
    if (props.requestId || props.embedded) {
        return;
    }
    
    // Merge updates into current filter
    const newFilter = { ...currentFilter.value, ...updates };
    
    // Reset to page 0 when filters change (except when page itself is being updated)
    if (!('page' in updates)) {
        newFilter.page = 0;
    }
    
    // 1. Update currentFilter
    currentFilter.value = newFilter;
    
    // 2. Save to storage (cache and database)
    saveFilterToStorage(newFilter);
    saveDatatableFilters(newFilter);
    
    // 3. Update URL
    const urlParams = buildUrlFromFilter(newFilter);
    const newUrl = `/work/list${urlParams ? '?' + urlParams : ''}`;
    router.replace(newUrl);
    
    // 4. Reload data
    loadData();
};

// ===== ROUTE WATCHER =====

// Watch route changes and reload data
watch(() => route.query, (newQuery, oldQuery) => {
    // Skip route watching in embedded mode
    if (props.requestId) return;
    
    // Parse new filter from URL
    const newFilter = parseUrlToFilter();
    
    // Check if filter actually changed (prevent infinite loops)
    const currentParams = buildUrlFromFilter(currentFilter.value);
    const newParams = buildUrlFromFilter(newFilter);
    
    if (currentParams !== newParams) {
        currentFilter.value = newFilter;
        
        // Save to cache
        saveFilterToStorage(newFilter);
        
        // Load data with new filter
        loadData();
    }
}, { deep: true });

// ===== HELPER FUNCTIONS =====

// Get client name by CustomerNumber
const getClientName = (customerNumber: number) => {
    const client = ClientList.value.find(c => c.CustomerNumber === customerNumber);
    return client?.Company || `Client #${customerNumber}`;
};

// Get tech name by ID
const getTechName = (techId: number) => {
    const tech = TechList.value.find(t => t.Id === techId);
    return tech?.TechnicianName || `Tech #${techId}`;
};

// Remove single client filter
const removeClientFilter = (customerNumber: number) => {
    setFilter({
        client: currentFilter.value.client.filter(c => c !== customerNumber)
    });
};

// Remove single technician filter
const removeTechFilter = (techId: number) => {
    setFilter({
        technician: currentFilter.value.technician.filter(t => t !== techId)
    });
};

// Remove date filter
const removeDateFilter = () => {
    setFilter({ startDate: '', endDate: '' });
};

const clearAllFilters = () => {
    setFilter({
        client: [],
        technician: [],
        searchTerm: '',
        startDate: '',
        endDate: '',
        sort: []
    });
};

// ===== LEGACY FUNCTION REPLACEMENTS =====

const actionDropdown = ref<Record<string, boolean>>({});

// Embedded mode state
const showEditModal = ref(false);
const editingRecordId = ref<string | null>(null);

const newRecord = () => {
    if (props.embedded) {
        editingRecordId.value = 'new';
        showEditModal.value = true;
    } else {
        router.push({
            path: "/work/new"
        });
    }
};

const refresh = () => {
    loadData();
};

const deleteRecord = (id: any) => {
    Alert.confirm("Are you sure you want to delete this record?", () => {
        console.log("Delete work entry:", id);
    }, () => {});
};

const editRecord = (id: any) => {
    if (props.embedded) {
        editingRecordId.value = id.toString();
        showEditModal.value = true;
    } else {
        router.push({
            path: "/work/edit/" + id
        });
    }
};

const closeEditModal = () => {
    showEditModal.value = false;
    editingRecordId.value = null;
    // Refresh the list after closing modal
    refresh();
};

// ===== CELL CLICK & ENTITY EDIT HANDLERS =====

const handleCellClick = (row: any, column: any, value: any, rowIndex: number) => {
    // Don't trigger edit for Select or Actions columns
    if (column.name !== 'Select' && column.name !== 'Actions') {
        editRecord(row.Id);
    }
};

const editClient = (id?: number) => {
    if (id) {
        Emitter.emit('open:clientForm', id);
    }
};

const editJob = (id?: number) => {
    if (id) {
        Emitter.emit('edit:job', id);
    }
};

const editTech = (id?: number) => {
    if (id) {
        Emitter.emit('open:techForm', id);
    }
};

// ===== ROW CLASS HELPER =====

// Get CSS class for table row based on status
const getRowClass = (row: TimeModel) => {
    const statusId = row.Status;
    if (!statusId) return '';
    
    const status = TaskStatusList.value[statusId];
    const statusName = status?.Status?.toLowerCase() || '';
    
    if (statusName.includes('completed') || statusName.includes('done') || statusName.includes('finished') || statusName.includes('closed')) {
        return 'row-status-completed';
    } else if (statusName.includes('pending') || statusName.includes('waiting') || statusName.includes('queued')) {
        return 'row-status-pending';
    } else if (statusName.includes('progress') || statusName.includes('active') || statusName.includes('working') || statusName.includes('started')) {
        return 'row-status-progress';
    } else if (statusName.includes('cancelled') || statusName.includes('rejected') || statusName.includes('failed')) {
        return 'row-status-cancelled';
    } else if (statusName.includes('scheduled') || statusName.includes('planned') || statusName.includes('assigned')) {
        return 'row-status-scheduled';
    } else if (statusName.includes('draft') || statusName.includes('new')) {
        return 'row-status-draft';
    }
    return '';
};

// ===== LOCATION HELPERS =====

const locationMap: Record<number, string> = {
    1: 'ON-SITE',
    2: 'GTA',
    3: 'WEB',
    4: 'PHONE/EMAIL',
    5: 'IN HOUSE',
    6: 'OVERSIGHT'
};

const getLocationName = (locationId: number | null | undefined): string => {
    if (!locationId) return '';
    return locationMap[locationId] || `#${locationId}`;
};

// ===== STATUS HELPERS =====

// Get status name by ID
const getStatusName = (statusId: number) => {
    if (statusId === -1) return 'Deleted';
    const status = TaskStatusList.value[statusId];
    return status?.Status || `Unknown`;
};

// Helper function to get Bootstrap badge class based on status
const getStatusBadgeClass = (statusId: number) => {
    const status = TaskStatusList.value[statusId];
    const statusName = status?.Status?.toLowerCase() || '';
    
    if (statusName.includes('completed') || statusName.includes('done') || statusName.includes('finished') || statusName.includes('closed')) {
        return 'bg-success';
    } else if (statusName.includes('pending') || statusName.includes('waiting') || statusName.includes('queued')) {
        return 'bg-warning';
    } else if (statusName.includes('progress') || statusName.includes('active') || statusName.includes('working') || statusName.includes('started')) {
        return 'bg-primary';
    } else if (statusName.includes('cancelled') || statusName.includes('rejected') || statusName.includes('failed')) {
        return 'bg-danger';
    } else if (statusName.includes('scheduled') || statusName.includes('planned') || statusName.includes('assigned')) {
        return 'bg-info';
    } else if (statusName.includes('draft') || statusName.includes('new')) {
        return 'bg-secondary';
    } else {
        return 'bg-light text-dark';
    }
};

// Helper function to get Font Awesome icon based on status
const getStatusIcon = (statusId: number) => {
    const status = TaskStatusList.value[statusId];
    const statusName = status?.Status?.toLowerCase() || '';
    
    if (statusName.includes('completed') || statusName.includes('done') || statusName.includes('finished') || statusName.includes('closed')) {
        return 'fas fa-check-circle';
    } else if (statusName.includes('pending') || statusName.includes('waiting') || statusName.includes('queued')) {
        return 'fas fa-clock';
    } else if (statusName.includes('progress') || statusName.includes('active') || statusName.includes('working') || statusName.includes('started')) {
        return 'fas fa-spinner fa-spin';
    } else if (statusName.includes('cancelled') || statusName.includes('rejected') || statusName.includes('failed')) {
        return 'fas fa-times-circle';
    } else if (statusName.includes('scheduled') || statusName.includes('planned') || statusName.includes('assigned')) {
        return 'fas fa-calendar-check';
    } else if (statusName.includes('draft') || statusName.includes('new')) {
        return 'fas fa-file';
    } else {
        return 'fas fa-circle';
    }
};

// ===== SELECTION MANAGEMENT =====

const toggleRowSelection = (rowId: number) => {
    const index = selectedRows.value.indexOf(rowId);
    if (index > -1) {
        selectedRows.value.splice(index, 1);
    } else {
        selectedRows.value.push(rowId);
    }
    updateSelectAllState();
};

const toggleSelectAll = () => {
    if (selectAll.value) {
        selectedRows.value = [];
        selectAll.value = false;
    } else {
        selectedRows.value = rows.value.map((row: any) => row.Id);
        selectAll.value = true;
    }
};

const updateSelectAllState = () => {
    if (rows.value.length === 0) {
        selectAll.value = false;
        return;
    }
    const allSelected = rows.value.every((row: any) => selectedRows.value.includes(row.Id));
    selectAll.value = allSelected;
};

const clearSelection = () => {
    selectedRows.value = [];
    selectAll.value = false;
};

// ===== BULK ACTIONS =====

const confirmBulkDelete = async () => {
    if (selectedRows.value.length === 0) return;
    
    console.warn(`Bulk delete for ${selectedRows.value.length} work entry(s) - API implementation needed`);
    
    showDeleteModal.value = false;
};

// ===== LIFECYCLE =====

onMounted(async () => {
    // Initialize filter and load data
    await initializeFilter();
    loadData();
});

// ===== DATATABLE MODEL =====

// KtDatatable model definition for Work list
const workModel: Array<KtDatatableModelProp> = [
    {
        name: "Select",
        label: "Select",
        type: "select",
        width: 60,
        locked: true,
        draggable: false
    },
    {
        name: "Id",
        label: "ID",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "Contact",
        label: "Client Code",
        display: true,
        type: "string"
    },
    {
        name: "Company",
        label: "Company",
        display: true,
        type: "string"
    },
    {
        name: "RequestId",
        label: "Request ID",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "TechnicianName",
        label: "Technician",
        display: true,
        type: "string"
    },
    {
        name: "TaskLocation",
        label: "Location",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "WorkCompleteDate",
        label: "Work Complete",
        display: true,
        type: "date",
        sortable: true
    },
    {
        name: "ActualTime",
        label: "Actual Hours",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "ActualWork",
        label: "Work Done",
        display: true,
        type: "string",
        truncateWords: 8
    },
    {
        name: "InvoiceTime",
        label: "Invoice Hours",
        display: false,
        type: "number",
        sortable: true
    },
    {
        name: "InvoiceAmount",
        label: "Invoice Amount",
        display: false,
        type: "number",
        sortable: true
    },
    {
        name: "InvoiceText",
        label: "Invoice Text",
        display: false,
        type: "string"
    },
    {
        name: "Status",
        label: "Status",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "ActualOvertime",
        label: "Overtime",
        display: false,
        type: "number",
        sortable: true
    },
    {
        name: "ShouldBill",
        label: "Should Bill",
        display: false,
        type: "number",
        sortable: true
    },
    {
        name: "PaidAmount",
        label: "Paid Amount",
        display: false,
        type: "number",
        sortable: true
    },
    {
        name: "CreateDate",
        label: "Created",
        display: false,
        type: "date",
        sortable: true
    },
    {
        name: "InvoiceNumber",
        label: "Invoice #",
        display: true,
        type: "string"
    }
];
    
</script>
